import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/cn";

type Variant = "primary" | "secondary" | "ghost" | "danger";

const VARIANTS: Record<Variant, string> = {
  primary:
    "bg-accent text-accent-ink border-accent shadow-sm hover:bg-accent-2 hover:border-accent-2",
  secondary:
    "bg-surface text-ink border-border shadow-sm hover:bg-surface-2 hover:border-muted-2",
  ghost: "bg-transparent text-ink-2 border-transparent hover:bg-surface-2",
  danger:
    "bg-transparent text-danger border-danger/35 hover:bg-danger-soft hover:border-danger/60",
};

/**
 * One button, four intents, one height. 28px rather than the 36–40px a
 * consumer app would use: this console puts three or four actions in a
 * table row, and a comfortable touch target there costs a row of density
 * on every screen.
 */
export function Button({
  children,
  variant = "secondary",
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  children: ReactNode;
  variant?: Variant;
}) {
  return (
    <button
      type="button"
      {...rest}
      className={cn(
        "inline-flex items-center justify-center gap-1.5 rounded-control border",
        "h-8 px-3 text-[12px] font-semibold transition-colors",
        "disabled:opacity-45 disabled:pointer-events-none",
        VARIANTS[variant],
        className,
      )}
    >
      {children}
    </button>
  );
}
